import React from 'react';
import PropTypes from 'prop-types';
import { View, Dimensions, StyleSheet } from 'react-native';
import Colors from '@global/colors';
import {
  convertProductMarketType,
  mappingProductCombination,
} from '@global/helpers';

export const dimension = Dimensions.get('screen');
export const actionWidth = dimension.width - 30;
export const limit = 5;
export const searchLimit = 50;
export const imageSize = 50;
export const LIMIT_AMOUNT = 2000;
export const VISIBILITY_SEARCH_KEY = 'search';

// Rows saved before promo_status only have is_promo (true = Implemented, false = No Promotion).
// No answer at all (is_promo null/missing) stays null.
export const resolvePromoStatus = (item) => {
  if (item?.promo_status) {
    return item.promo_status;
  }
  if (item?.is_promo === true) {
    return 'implemented';
  }
  if (item?.is_promo === false) {
    return 'no_promotion';
  }
  return null;
};
export const createEmptyVisibilityBrowseState = () => ({
  brands: [],
  productsByBrand: {},
  skip: { result: 0 },
  empty: {},
  selectedBrand: [],
});

const isPresent = (value) =>
  value !== undefined && value !== null && value !== '';

const pickFirstPresent = (...values) => {
  return values.find((value) => isPresent(value));
};

const getPrimaryProductId = (product = {}) => {
  return pickFirstPresent(
    product?.product_id,
    product?.id,
    product?.product?.id,
    product?.productId,
    '',
  );
};

export const getVisibilityProductIdentifier = (product = {}) => {
  return getPrimaryProductId(product);
};

const getPrimaryCombinationId = (combination = {}, product = {}) => {
  return pickFirstPresent(
    combination?.id,
    combination?._id,
    combination?.combination_id,
    combination?.sku,
    combination?.reference,
    product?.product_combination_id,
    product?.combination_id,
    '',
  );
};

export const getVisibilityCombinationIdentifier = (product = {}) => {
  return getPrimaryCombinationId(product?.combination, product);
};

const getPrimaryProductName = (product = {}) => {
  return pickFirstPresent(
    product?.name,
    product?.product?.name,
    product?.product_name,
    '',
  );
};

export const getVisibilityProductName = (product = {}) => {
  return getPrimaryProductName(product);
};

const getPrimaryBrand = (product = {}) => {
  if (product?.brand) {
    return product.brand;
  }
  if (product?.product?.brand) {
    return product.product.brand;
  }
  if (product?.brand_id) {
    return { id: product.brand_id, name: '' };
  }
  return { id: '', name: '' };
};

export const getVisibilityProductBrand = (product = {}) => {
  return getPrimaryBrand(product);
};

const getCoverImageUrl = (images = []) => {
  if (!Array.isArray(images) || !images.length) {
    return '';
  }

  const coverImage = images.find((image) => image?.is_cover && image?.url);
  if (coverImage?.url) {
    return coverImage.url;
  }

  const firstImage = images.find((image) => image?.url);
  return firstImage?.url ?? '';
};

const getPrimaryImage = (product = {}) => {
  return pickFirstPresent(
    getCoverImageUrl(product?.images),
    product?.image,
    getCoverImageUrl(product?.product?.images),
    product?.product?.image,
    getCoverImageUrl(product?.combination?.images),
    product?.combination?.image,
    '',
  );
};

export const getVisibilityProductImage = (product = {}) => {
  return getPrimaryImage(product);
};

export const normalizeVisibilityBrandList = (brands = []) => {
  return (brands ?? [])
    .filter((item) => item?._id)
    .map((item) => ({
      _id: item._id,
      name: item?.name ?? '',
    }));
};

export const sanitizeVisibilityProductsByBrand = (productsByBrand = {}) => {
  return Object.entries(productsByBrand ?? {}).reduce((acc, [key, value]) => {
    if (key !== 'other_search') {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const sanitizeVisibilityBrowseMap = (
  value = {},
  excludedKey = VISIBILITY_SEARCH_KEY,
) => {
  return Object.entries(value ?? {}).reduce((acc, [key, mapValue]) => {
    if (key !== excludedKey) {
      acc[key] = mapValue;
    }
    return acc;
  }, {});
};

export const sanitizeVisibilityBrowseSkip = (value = {}) => {
  const nextSkip = sanitizeVisibilityBrowseMap(value);
  if (nextSkip.result === undefined) {
    nextSkip.result = 0;
  }
  return nextSkip;
};

export const sanitizeVisibilityBrowseEmpty = (value = {}) => {
  return sanitizeVisibilityBrowseMap(value);
};

export const applyExpandedVisibilityBrands = (
  brands = [],
  expandedBrandIds = [],
) => {
  const expandedSet = new Set(expandedBrandIds ?? []);
  return normalizeVisibilityBrandList(brands).map((brand) => ({
    ...brand,
    _expanded: expandedSet.has(brand._id),
  }));
};

export const buildVisibilityBrowseSnapshot = ({
  brands = [],
  productsByBrand = {},
  skip = { result: 0 },
  empty = {},
  selectedBrand = [],
} = {}) => {
  return {
    brands: normalizeVisibilityBrandList(brands),
    productsByBrand: sanitizeVisibilityProductsByBrand(productsByBrand),
    skip: sanitizeVisibilityBrowseSkip(skip),
    empty: sanitizeVisibilityBrowseEmpty(empty),
    selectedBrand: (selectedBrand ?? []).filter(Boolean),
  };
};

export const convertProductResult = (
  pr,
  isFromStorage = false,
  isFromDB = false,
) => {
  const next = { ...pr };
  next.id = getPrimaryProductId(pr);
  next.name = getVisibilityProductName(pr);
  next.brand = getVisibilityProductBrand(pr);
  if (!next.combination && pr?.combinations?.length) {
    next.combination = pr.combinations[0];
  }
  if (next?.combination && !next.combination.id) {
    next.combination.id = getPrimaryCombinationId(next.combination, pr);
  }
  next.image = getVisibilityProductImage(pr);
  next.is_selected = true;
  next.is_saved = true;
  next.isFromStorage = isFromStorage;
  next.isFromDB = Boolean(pr?.isFromDB ?? isFromDB);
  next.vu_id = pickFirstPresent(pr?._id, '');
  next.is_initiate = !pr.isFromDB;
  return next;
};

export const getVisibilityProductReference = (product = {}) => {
  return pickFirstPresent(
    product?.combination?.reference,
    product?.reference,
    '',
  );
};

export const getVisibilityProductIdentity = (product = {}) => {
  const productId = getVisibilityProductIdentifier(product);
  const combinationId = getVisibilityCombinationIdentifier(product);
  return `${productId}-${combinationId}`;
};

export const dedupeVisibilityProducts = (products = []) => {
  const visited = new Set();

  return (products ?? []).filter((product) => {
    const identity = getVisibilityProductIdentity(product);
    if (!identity || identity === '-') {
      return true;
    }
    if (visited.has(identity)) {
      return false;
    }
    visited.add(identity);
    return true;
  });
};

export const mergeProductsByCombination = (products = []) => {
  const combinationMap = new Map();
  const productMap = new Map();

  products.forEach((input, index) => {
    if (!input) {
      return;
    }

    const item = JSON.parse(JSON.stringify(input));
    const combos = Array.isArray(item?.combinations)
      ? item.combinations.filter(Boolean)
      : item?.combination
      ? [item.combination]
      : [];

    const productId = getPrimaryProductId(item);
    const productKey = productId || `${item?.brand?.id ?? 'brand'}-${index}`;

    if (!item?.id && productId) {
      item.id = productId;
    }

    if (!item?.name) {
      item.name = getVisibilityProductName(item);
    }

    let targetProduct = productMap.get(productKey);
    if (!targetProduct) {
      targetProduct = { ...item, combinations: [] };
      productMap.set(productKey, targetProduct);
    }

    combos.forEach((combo, comboIndex) => {
      if (!combo) {
        return;
      }

      const combinationId = getPrimaryCombinationId(combo, item);
      const combinationKey = `${productKey}-${combinationId ?? comboIndex}`;

      if (combinationMap.has(combinationKey)) {
        return;
      }

      combinationMap.set(combinationKey, true);
      if (!Array.isArray(targetProduct.combinations)) {
        targetProduct.combinations = [];
      }
      targetProduct.combinations.push(JSON.parse(JSON.stringify(combo)));
    });

    if (!targetProduct.combination && targetProduct.combinations?.length) {
      targetProduct.combination = targetProduct.combinations[0];
    }
  });

  return Array.from(productMap.values());
};

export const getProductCombinations = (product = {}) => {
  if (Array.isArray(product?.combinations) && product.combinations.length) {
    return product.combinations.filter(Boolean);
  }
  if (product?.combination) {
    return [product.combination];
  }
  return [];
};

export const flattenVisibilitySearchResponse = (rawData) => {
  if (Array.isArray(rawData)) {
    return rawData;
  }
  if (rawData && typeof rawData === 'object') {
    return Object.values(rawData).reduce((acc, value) => {
      if (Array.isArray(value)) {
        acc.push(...value);
      }
      return acc;
    }, []);
  }
  return [];
};

export const applyExactEanMatch = (products = [], keyword = '') => {
  const trimmedKeyword = keyword?.trim();
  if (!trimmedKeyword) {
    return products;
  }

  const matchedProducts = products.filter((product) =>
    getProductCombinations(product).some((combination) => {
      const eanNo = (combination?.ean_no ?? '').toString().trim();
      return eanNo === trimmedKeyword;
    }),
  );

  return matchedProducts.length ? matchedProducts : products;
};

export const filterVisibilityProductsByKeyword = (
  products = [],
  keyword = '',
) => {
  const trimmedKeyword = keyword?.trim()?.toLowerCase();
  if (!trimmedKeyword) {
    return products;
  }

  const normalizedKeyword = trimmedKeyword.replace(/\s+/g, '');

  return (products ?? []).filter((product) => {
    const productName = getVisibilityProductName(product)?.toLowerCase() ?? '';
    const brandName =
      getVisibilityProductBrand(product)?.name?.toLowerCase() ?? '';
    const reference =
      getVisibilityProductReference(product)?.toLowerCase() ?? '';
    const combinations = getProductCombinations(product);
    const hasMatchingEan = combinations.some((combination) => {
      const rawEan = (combination?.ean_no ?? '').toString().toLowerCase();
      const normalizedEan = rawEan.replace(/\s+/g, '');
      if (!normalizedEan) {
        return false;
      }
      return (
        normalizedEan === normalizedKeyword ||
        normalizedEan.includes(normalizedKeyword)
      );
    });

    return (
      productName.includes(trimmedKeyword) ||
      brandName.includes(trimmedKeyword) ||
      reference.includes(trimmedKeyword) ||
      hasMatchingEan
    );
  });
};

export const normalizeVisibilityApiProducts = (
  products = [],
  { markFromDB = false, splitCombinations = false } = {},
) => {
  return (products ?? []).reduce((acc, item) => {
    if (!item) {
      return acc;
    }

    const convertedProduct = convertProductMarketType(item);
    const normalizedProduct = markFromDB
      ? { ...convertedProduct, isFromDB: true }
      : convertedProduct;

    if (!splitCombinations) {
      acc.push(normalizedProduct);
      return acc;
    }

    const combinations = getProductCombinations(normalizedProduct);
    if (!combinations.length) {
      acc.push(normalizedProduct);
      return acc;
    }

    combinations.forEach((combination) => {
      acc.push({
        ...normalizedProduct,
        combination,
        combinations: [combination],
      });
    });

    return acc;
  }, []);
};

export const formatVisibilityProductsForList = (products = []) => {
  return (products ?? []).reduce((acc, product) => {
    if (!product) {
      return acc;
    }

    const normalizedProduct = { ...product };
    const combinations = getProductCombinations(normalizedProduct);

    if (normalizedProduct?.product?.id) {
      normalizedProduct.id = normalizedProduct.product.id;
    }
    if (normalizedProduct?.product?.name) {
      normalizedProduct.name = normalizedProduct.product.name;
    }

    combinations.forEach((combination) => {
      const formattedProduct = mappingProductCombination(
        { ...normalizedProduct },
        combination,
      );
      formattedProduct.combinations = [combination];
      acc.push(formattedProduct);
    });

    return acc;
  }, []);
};

export const expandVisibilityProductsByCombination = (products = []) => {
  return (products ?? []).reduce((acc, product) => {
    const combinations = getProductCombinations(product);

    combinations.forEach((combination) => {
      acc.push({
        ...product,
        combination,
        combinations: [combination],
      });
    });

    return acc;
  }, []);
};

export const extractVisibilitySelectedProducts = (products = []) => {
  return (products ?? []).reduce((acc, product) => {
    const combinations = getProductCombinations(product);

    combinations.forEach((combination) => {
      const selectedTask = product?.selected_in_task;
      const selectedProductId = getPrimaryProductId(product);
      const selectedCombinationId = getPrimaryCombinationId(
        combination,
        product,
      );

      if (
        selectedTask &&
        selectedTask?.id == selectedProductId &&
        selectedTask?.combination_id == selectedCombinationId
      ) {
        acc.push({
          ...product,
          product_id: selectedTask.id,
          combination,
          quantity: selectedTask.quantity,
        });
      }
    });

    return acc;
  }, []);
};

export const buildVisibilityProductBuckets = (products = []) => {
  const productsByBrand = {};
  const brandMap = new Map();

  dedupeVisibilityProducts(products).forEach((product) => {
    if (product?.combination?.is_other) {
      if (!Array.isArray(productsByBrand.other_search)) {
        productsByBrand.other_search = [];
      }
      productsByBrand.other_search.push(product);
      return;
    }

    const brandId = product?.brand?.id;
    if (!brandId) {
      return;
    }

    if (!Array.isArray(productsByBrand[brandId])) {
      productsByBrand[brandId] = [];
      brandMap.set(brandId, {
        _id: brandId,
        name: product?.brand?.name ?? '',
      });
    }

    productsByBrand[brandId].push(product);
  });

  return {
    brands: Array.from(brandMap.values()),
    productsByBrand,
  };
};

export const productKeyExtractor = (item, index = 0) => {
  const productId = getPrimaryProductId(item) || `product-${index}`;
  const combination = item?.combination ?? item?.combinations?.[0] ?? {};
  const combinationId =
    getPrimaryCombinationId(combination, item) || `combo-${index}`;

  return `${productId}-${combinationId}-${index}`;
};

export const Skeleton = ({ width, height, style }) => {
  return (
    <View style={[styles.skeleton, { width, height }, style]}>
      <View style={styles.skeletonInner} />
    </View>
  );
};

Skeleton.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.any,
};

export const getComboObj = (item) => {
  if (item?.combination) {
    return item.combination;
  }
  if (Array.isArray(item?.combinations) && item.combinations.length) {
    return item.combinations[0];
  }
  return item;
};

export const getProductId = (item) => getPrimaryProductId(item);

export const ensureNormalized = (obj, isFromStorage, isFromDB) => {
  const combo = getComboObj(obj) ?? {};
  const normalized = {
    ...obj,
    id: getProductId(obj),
    product_id: getProductId(obj),
    combination: {
      ...combo,
      id: getPrimaryCombinationId(combo, obj),
    },
  };
  if (normalized.isFromStorage === undefined) {
    normalized.isFromStorage = isFromStorage;
  }
  if (normalized.isFromDB === undefined) {
    normalized.isFromDB = isFromDB;
  }

  return convertProductResult(
    normalized,
    normalized.isFromStorage,
    normalized.isFromDB,
  );
};

const toNumberIfDefined = (val) => {
  if (val === undefined || val === null || val === '') {
    return undefined;
  }
  const num = Number(val);
  return isNaN(num) ? undefined : num;
};

// B2B-5929: isValueSellOutOnly (promotor + online) keeps the typed value_sell_out
// instead of the price x sell_out calculation
const normalizeVisibilityProductFields = (
  products = [],
  isValueSellOutOnly = false,
) => {
  return (products ?? []).map((p) => ({
    ...p,
    id: getPrimaryProductId(p),
    name: getPrimaryProductName(p),
    sell_in: toNumberIfDefined(p?.sell_in),
    sell_out: toNumberIfDefined(p?.sell_out),
    return: toNumberIfDefined(p?.return),
    result_quantity: toNumberIfDefined(p?.result_quantity ?? p?.endingStock),
    pricing: toNumberIfDefined(p?.pricing) ?? 0,
    expires_in_3_months: toNumberIfDefined(p?.expires_in_3_months) ?? 0,
    expires_in_12_months: toNumberIfDefined(p?.expires_in_12_months) ?? 0,
    valueSellOut: p?.valueSellOut ?? { default: 0, final_price: 0 },
    value_sell_out: isValueSellOutOnly
      ? toNumberIfDefined(p?.value_sell_out) ?? 0
      : p?.valueSellOut?.final_price ??
        toNumberIfDefined(p?.value_sell_out) ??
        0,
    edited_result_quantity:
      p?.edited_result_quantity === '' || p?.edited_result_quantity === null
        ? undefined
        : p?.edited_result_quantity,
    edited_reason_result_quantity: p?.edited_reason_result_quantity ?? '',
    is_ending_edited: !!p?.is_ending_edited,
    promo_status: resolvePromoStatus(p),
    promo_list: p?.promo_list ?? [],
    is_wobbler: p?.is_wobbler ?? null,
    notes: p?.notes ?? '',
    combination: {
      id: getPrimaryCombinationId(p?.combination, p),
      reference: pickFirstPresent(p?.combination?.reference, p?.reference, ''),
      is_other: Boolean(p?.is_other ?? p?.combination?.is_other),
    },
    is_selected: true,
    is_saved: true,
  }));
};

const mapVisibilityProductsToPayload = (
  payloadProducts = [],
  isValueSellOutOnly = false,
) => {
  return (payloadProducts ?? [])
    .filter((pr) => Boolean(getVisibilityCombinationIdentifier(pr)))
    .map((pr) => {
      const quantityBeginning = Number(
        pr?.quantity ?? pr?.stock ?? pr?.result_quantity ?? 0,
      );

      return {
        product_name: getPrimaryProductName(pr),
        product_id: getVisibilityProductIdentifier(pr),
        combination_id: getVisibilityCombinationIdentifier(pr),
        quantity: !pr?.is_beginning
          ? toNumberIfDefined(pr?.stock) ??
            Number(
              pr?.endingStock ||
                pr?.result_quantity -
                  (pr?.sell_in !== '0' ? pr?.sell_in : 0) +
                  (pr?.sell_out !== '0' ? pr?.sell_out : 0) +
                  (pr?.return !== '0' ? pr?.return : 0),
            )
          : quantityBeginning,
        pricing: Number(pr?.pricing) || 0,
        ...(!pr?.is_beginning
          ? {
              sell_in: pr?.sell_in === '0' ? 0 : pr?.sell_in,
              sell_out: pr?.sell_out === '0' ? 0 : pr?.sell_out,
              return: pr?.return === '0' ? 0 : pr?.return,
              result_quantity:
                Number(pr?.endingStock) || Number(pr?.result_quantity) || 0,
              value_sell_out:
                !isValueSellOutOnly && pr?.valueSellOut?.final_price
                  ? Number(pr.valueSellOut.final_price)
                  : pr?.value_sell_out
                  ? Number(pr.value_sell_out)
                  : 0,
              edited_result_quantity: pr?.edited_result_quantity,
              edited_reason_result_quantity: pr?.edited_reason_result_quantity,
              is_ending_edited: pr?.is_ending_edited ? true : false,
            }
          : {}),
        is_beginning: pr?.is_beginning,
        expires_in_3_months: Number(pr?.expires_in_3_months) || 0,
        expires_in_12_months: Number(pr?.expires_in_12_months) || 0,
        promo_status: resolvePromoStatus(pr),
        promo_list: pr?.promo_list ?? [],
        is_wobbler: pr?.is_wobbler ?? false,
        notes: pr?.notes ?? '',
        ...(pr?.combination?.is_other ? { is_other: true } : false),
      };
    });
};

export const buildVisibilityPayloadProducts = ({
  defaultProducts = [],
  otherProducts = [],
  isValueSellOutOnly = false,
} = {}) => {
  const syncedDefault = normalizeVisibilityProductFields(
    defaultProducts,
    isValueSellOutOnly,
  );
  const syncedOther = normalizeVisibilityProductFields(
    otherProducts,
    isValueSellOutOnly,
  );

  const otherPayloadProducts = syncedOther.map((pr) => {
    const product = { ...pr };
    delete product.is_selected;
    delete product.is_saved;
    product.is_other = true;
    return product;
  });

  const productsRaw = syncedDefault.concat(
    syncedOther.map((pr) => ({
      ...pr,
      is_other: true,
      combination: {
        ...pr.combination,
        is_other: true,
      },
    })),
  );

  const payloadProducts = syncedDefault
    .map((pr) => {
      const product = { ...pr };
      delete product.is_selected;
      delete product.is_saved;
      return product;
    })
    .concat(otherPayloadProducts);

  return {
    products: mapVisibilityProductsToPayload(
      payloadProducts,
      isValueSellOutOnly,
    ),
    products_raw: productsRaw,
    payloadProducts,
  };
};

const styles = StyleSheet.create({
  skeleton: {
    borderRadius: 4,
    backgroundColor: Colors.grey0,
    overflow: 'hidden',
    marginVertical: 4,
  },
  skeletonInner: {
    flex: 1,
    backgroundColor: Colors.grey0,
  },
});
